import React, { useCallback, useEffect, useRef, useState } from 'react';
import { styled } from '@mui/material/styles';

import IconButton from '@mui/material/IconButton';

import ModalDialog from '@/components/ModalDialog';
import CrossIcon from '@/components/icons/Cross';
import ModelUploader from './ModelUploader';
import { FileAccumulator } from './FileAccumulator';
import { ProjectData } from '@/state/types/project-data';
import t from '@/utils/translate';

type ModelUploadDlgProps = {
  project: ProjectData,
  files?: File[],
  open: boolean,
  onClose: () => void
};

const DialogHeaderBar = styled('h1')({
  fontWeight: 500,
  fontSize: '20px',
  margin: 0,
  padding: 0,
  color: '#091E42',
  lineHeight: '23.44px',
  position: 'relative',
  height: '23px',
});

const CloseButton = styled(IconButton)({
  width: '32px',
  height: '32px',
  padding: '0px',
  position: 'absolute',
  right: '-4px',
  top: '-4px',
  fontSize: '18px',
  color: 'inherit',
  opacity: 0.2,
  ['&:hover']: {
    opacity: 1,
    backgroundColor: 'transparent',
  },
});

export default function ({ project, files, open, onClose }: ModelUploadDlgProps) {
  // const CONFIRM_TEXT = t('model.upload.message.confirmReload');

  /*
  const navBlocker = useBlocker(
    ({ currentLocation, nextLocation }) => 
      !!busy && currentLocation.pathname !== nextLocation.pathname
  );
  */

  const closeHandler = useCallback(() => {
    onClose();
  }, []);

  const { current: accumulator } = useRef<FileAccumulator>(new FileAccumulator());

  const [, setTimestamp] = useState<number>(Date.now());

  const forceUpdate = useCallback(() => {
    setTimestamp(Date.now());
  }, []);

  useEffect(() => {
    function ondrop(e: DragEvent) {
      e.preventDefault();
      e.stopPropagation();
      if (e.dataTransfer?.files) {
        for (let f of e.dataTransfer.files) {
          const ext = ((f.name.match(/\.\w+$/i) || [])[0] || '').toLowerCase();
          if (['.bim', '.rvt'].includes(ext)) {
            accumulator.add(f);
          }
        }
      }
      forceUpdate();
    }
    function ondragover(e: DragEvent) {
      e.preventDefault();
      e.stopPropagation();
    }
    document.addEventListener('drop', ondrop);
    document.addEventListener('dragover', ondragover);
    return (): void => {
      document.removeEventListener('drop', ondrop);
      document.removeEventListener('dragover', ondragover);
    }
  }, []);

  useEffect(() => {
    if (files && files.length) {
      files.forEach((f) => {
        accumulator.add(f);
      });
      forceUpdate();
    }
  }, [files]);

  return (
    <ModalDialog
      open={open}
      withoutHeader
      maxWidth="none"
      maxHeight="calc(100% - 120px)"
      onClose={closeHandler}
    >
      <ModelUploader
        project={project}
        files={accumulator.empty ? undefined : accumulator.files}
      >
        <DialogHeaderBar>
          {t('model.title.upload')}
          <CloseButton onClick={closeHandler}>
            <CrossIcon/>
          </CloseButton>
        </DialogHeaderBar>
      </ModelUploader>
    </ModalDialog>
  );
}